import { useEffect, useMemo, useState } from "react";
import { Trash2, Plus, FileText, CheckCircle2, Download, Mail, Euro } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatEuro, formatEuro2, todayISO } from "@/lib/constants";

const INVOICE_STATUSES = ["Draft", "Sent", "Partially Paid", "Paid", "Cancelled"];

const blank = () => ({
  order_id: "",
  customer_id: "",
  customer_name: "",
  customer_address: "",
  customer_vat_number: "",
  customer_email: "",
  customer_phone: "",
  applied_price_level: "Standard",
  invoice_date: todayISO(),
  due_date: "",
  payment_terms: "14 days",
  customer_reference: "",
  items: [],
  delivery_fee: 0,
  vat_pct: 13.5,
  iban: "",
  bic: "",
  notes: "",
  status: "Draft",
});

/**
 * Reusable Invoice editor.
 * Props:
 *   open (bool), onClose(), orderId (string) -> loads draft for this order; or existingInvoice (obj) to edit.
 *   onSaved(invoice) -> callback after successful create/update
 */
export default function InvoiceEditor({ open, onClose, orderId, existingInvoice, onSaved }) {
  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);           // becomes the persisted invoice after save
  const [partialAmount, setPartialAmount] = useState("");

  useEffect(() => {
    if (!open) return;
    setSaved(null);
    setPartialAmount("");
    if (existingInvoice) {
      setForm({
        ...blank(),
        ...existingInvoice,
        items: (existingInvoice.items || []).map((it) => ({
          product_id: it.product_id || "",
          product_name: it.product_name || "",
          quantity: Number(it.quantity) || 0,
          unit_price: Number(it.unit_price) || 0,
        })),
      });
      // Show the payment/status panel immediately for existing invoices so the user
      // can edit status/register payment without re-saving.
      setSaved(existingInvoice);
    } else if (orderId) {
      api.invoiceDraftFromOrder(orderId).then((draft) => setForm({ ...blank(), ...draft }))
        .catch(() => toast.error("Failed to load order draft"));
    } else {
      setForm(blank());
    }
  }, [open, orderId, existingInvoice]);

  const patch = (obj) => setForm((f) => ({ ...f, ...obj }));

  const setItem = (idx, kv) => {
    setForm((f) => {
      const items = f.items.map((it, i) => (i === idx ? { ...it, ...kv } : it));
      return { ...f, items };
    });
  };
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { product_id: "", product_name: "", quantity: 1, unit_price: 0 }] }));

  const totals = useMemo(() => {
    const subtotal = form.items.reduce((a, it) => a + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);
    const delivery = Number(form.delivery_fee) || 0;
    const vatPct = Number(form.vat_pct) || 0;
    const vatBase = subtotal + delivery;
    const vat = vatBase * (vatPct / 100);
    const total = vatBase + vat;
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      delivery: Math.round(delivery * 100) / 100,
      vat: Math.round(vat * 100) / 100,
      total: Math.round(total * 100) / 100,
    };
  }, [form.items, form.delivery_fee, form.vat_pct]);

  const save = async () => {
    if (form.items.length === 0) return toast.error("Add at least one line item");
    setSaving(true);
    try {
      const payload = { ...form, delivery_fee: Number(form.delivery_fee) || 0, vat_pct: Number(form.vat_pct) || 0, status: form.status || "Draft" };
      const inv = existingInvoice
        ? await api.updateInvoice(existingInvoice.id, payload)
        : await api.createInvoice(payload);
      setSaved(inv);
      toast.success(`Invoice ${inv.invoice_number} ${existingInvoice ? "updated" : "created"}`);
      if (onSaved) onSaved(inv);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save invoice");
    } finally {
      setSaving(false);
    }
  };

  const download = async () => {
    if (!saved) return;
    try { await api.downloadInvoicePdf(saved.id, saved.invoice_number); }
    catch { toast.error("PDF download failed"); }
  };

  const sendEmail = async () => {
    if (!saved) return;
    try {
      const res = await api.sendInvoiceEmail(saved.id);
      toast.success(`Invoice sent to ${res.recipient}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send email");
    }
  };

  const markPaid = async () => {
    if (!saved) return;
    try {
      const inv = await api.markInvoicePaid(saved.id);
      setSaved(inv);
      if (onSaved) onSaved(inv);
      toast.success("Invoice marked paid — order updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const changeStatus = async (newStatus) => {
    if (!saved) return;
    try {
      const inv = await api.changeInvoiceStatus(saved.id, newStatus);
      setSaved(inv);
      if (onSaved) onSaved(inv);
      toast.success(`Status → ${inv.status}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to change status"); }
  };

  const registerPartial = async () => {
    if (!saved) return;
    const amt = Number(partialAmount);
    if (!amt || amt <= 0) return toast.error("Enter a positive amount");
    try {
      const inv = await api.registerInvoicePayment(saved.id, amt);
      setSaved(inv);
      setPartialAmount("");
      if (onSaved) onSaved(inv);
      toast.success(`Payment registered — status: ${inv.status}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const readOnly = !!saved || existingInvoice?.status === "Paid";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="invoice-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <FileText size={18} className="text-[#E85D04]" />
            {saved ? `Invoice ${saved.invoice_number}` : existingInvoice ? `Edit ${existingInvoice.invoice_number}` : "Create Invoice"}
          </DialogTitle>
        </DialogHeader>

        {/* Bill-to */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <Field label="Customer">
            <Input value={form.customer_name || ""} readOnly className="bg-[#F7F1E8]/40" data-testid="invoice-customer-name" />
          </Field>
          <Field label="Invoice Date">
            <Input type="date" value={form.invoice_date || ""}
              onChange={(e) => patch({ invoice_date: e.target.value })}
              disabled={readOnly}
              data-testid="invoice-date" />
          </Field>
          <Field label="Billing Address" className="sm:col-span-2">
            <Textarea rows={2} value={form.customer_address || ""}
              onChange={(e) => patch({ customer_address: e.target.value })}
              disabled={readOnly}
              placeholder="Street, Postal code City"
              data-testid="invoice-address" />
          </Field>
          <Field label="VAT Number">
            <Input value={form.customer_vat_number || ""}
              onChange={(e) => patch({ customer_vat_number: e.target.value })}
              disabled={readOnly}
              placeholder="FI12345678"
              data-testid="invoice-vat-number" />
          </Field>
          <Field label="Due Date">
            <Input type="date" value={form.due_date || ""}
              onChange={(e) => patch({ due_date: e.target.value })}
              disabled={readOnly}
              data-testid="invoice-due-date" />
          </Field>
          <Field label="Payment Terms">
            <Input value={form.payment_terms || ""}
              onChange={(e) => patch({ payment_terms: e.target.value })}
              disabled={readOnly}
              placeholder="14 days"
              data-testid="invoice-payment-terms" />
          </Field>
          <Field label="Customer Reference">
            <Input value={form.customer_reference || ""}
              onChange={(e) => patch({ customer_reference: e.target.value })}
              disabled={readOnly}
              placeholder="(Optional)"
              data-testid="invoice-customer-reference" />
          </Field>
          <Field label="Customer Email">
            <Input value={form.customer_email || ""}
              onChange={(e) => patch({ customer_email: e.target.value })}
              disabled={readOnly}
              placeholder="client@example.com"
              data-testid="invoice-customer-email" />
          </Field>
          <Field label="Customer Phone">
            <Input value={form.customer_phone || ""}
              onChange={(e) => patch({ customer_phone: e.target.value })}
              disabled={readOnly}
              placeholder="+358 …"
              data-testid="invoice-customer-phone" />
          </Field>
        </div>

        {/* Line items */}
        <div className="border-t border-[#E5E5E5] pt-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#6C757D]">Line items</Label>
            {!readOnly && (
              <Button variant="ghost" size="sm" onClick={addItem} data-testid="invoice-add-item">
                <Plus size={14} className="mr-1" /> Add line
              </Button>
            )}
          </div>
          <div className="space-y-2">
            {form.items.map((it, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-[#F7F1E8]/40 rounded px-2 py-2" data-testid={`invoice-item-${idx}`}>
                <Input className="col-span-5" value={it.product_name || ""}
                  onChange={(e) => setItem(idx, { product_name: e.target.value })}
                  disabled={readOnly}
                  placeholder="Product / description"
                  data-testid={`invoice-item-${idx}-name`} />
                <Input type="number" min="0" step="0.5" className="col-span-2" value={it.quantity ?? ""}
                  onChange={(e) => setItem(idx, { quantity: e.target.value === "" ? "" : Number(e.target.value) })}
                  disabled={readOnly}
                  placeholder="Qty"
                  data-testid={`invoice-item-${idx}-qty`} />
                <Input type="number" min="0" step="0.01" className="col-span-2" value={it.unit_price ?? ""}
                  onChange={(e) => setItem(idx, { unit_price: e.target.value === "" ? "" : Number(e.target.value) })}
                  disabled={readOnly}
                  placeholder="€ Unit"
                  data-testid={`invoice-item-${idx}-price`} />
                <div className="col-span-2 text-right font-semibold text-[#1F4D3A]">
                  {formatEuro2((Number(it.quantity) || 0) * (Number(it.unit_price) || 0))}
                </div>
                {!readOnly && (
                  <Button variant="ghost" size="icon" className="col-span-1 text-[#E76F51] hover:bg-[#E76F51]/10"
                    onClick={() => removeItem(idx)} data-testid={`invoice-item-${idx}-remove`}>
                    <Trash2 size={16} />
                  </Button>
                )}
              </div>
            ))}
            {form.items.length === 0 && (
              <div className="text-sm text-[#6C757D] italic text-center py-4">No line items — click &quot;Add line&quot;.</div>
            )}
          </div>
        </div>

        {/* Fees + VAT + payment details */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
          <Field label="Delivery Fee (€)">
            <Input type="number" min="0" step="0.5" value={form.delivery_fee ?? 0}
              onChange={(e) => patch({ delivery_fee: e.target.value === "" ? 0 : Number(e.target.value) })}
              disabled={readOnly}
              data-testid="invoice-delivery-fee" />
          </Field>
          <Field label="VAT %">
            <Input type="number" min="0" step="0.5" value={form.vat_pct ?? 13.5}
              onChange={(e) => patch({ vat_pct: e.target.value === "" ? 0 : Number(e.target.value) })}
              disabled={readOnly}
              data-testid="invoice-vat-input" />
          </Field>
          <Field label="Payment Account (IBAN)">
            <Input value={form.iban || ""} onChange={(e) => patch({ iban: e.target.value })}
              disabled={readOnly} data-testid="invoice-iban" />
          </Field>
        </div>

        {/* Totals */}
        <div className="bg-[#F7F1E8] rounded p-4 space-y-1.5 text-sm mb-4">
          <div className="flex justify-between text-[#6C757D]">
            <span>Subtotal (excl. VAT)</span>
            <span className="text-[#222] font-semibold" data-testid="invoice-subtotal">{formatEuro2(totals.subtotal)}</span>
          </div>
          {totals.delivery > 0 && (
            <div className="flex justify-between text-[#6C757D]">
              <span>Delivery fee</span>
              <span className="text-[#222] font-semibold">{formatEuro2(totals.delivery)}</span>
            </div>
          )}
          <div className="flex justify-between text-[#6C757D]">
            <span>VAT ({form.vat_pct}%)</span>
            <span className="text-[#222] font-semibold" data-testid="invoice-vat-amount">{formatEuro2(totals.vat)}</span>
          </div>
          <div className="border-t border-[#E5E5E5] pt-1.5 flex justify-between font-display text-xl">
            <span className="text-[#1F4D3A] font-bold">TOTAL</span>
            <span className="text-[#E85D04] font-bold" data-testid="invoice-total">{formatEuro2(totals.total)}</span>
          </div>
        </div>

        <Field label="Notes">
          <Textarea rows={2} value={form.notes || ""} onChange={(e) => patch({ notes: e.target.value })} disabled={readOnly} />
        </Field>

        {/* Applied price level tag + initial status */}
        {form.applied_price_level && (
          <div className="mt-3 flex items-center gap-3 text-xs">
            <span className="text-[#6C757D] uppercase tracking-wider font-bold">Price Level:</span>
            <span
              className="px-2 py-1 rounded font-bold uppercase tracking-wider"
              style={{
                backgroundColor: form.applied_price_level === "Partner Level 2" ? "#1F4D3A20" : form.applied_price_level === "Partner Level 1" ? "#E85D0420" : "#6C757D20",
                color: form.applied_price_level === "Partner Level 2" ? "#1F4D3A" : form.applied_price_level === "Partner Level 1" ? "#E85D04" : "#6C757D",
              }}
              data-testid="invoice-applied-price-level"
            >
              {form.applied_price_level}
            </span>
            {!saved && !existingInvoice && (
              <div className="ml-auto flex items-center gap-2">
                <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#6C757D]">Initial status</Label>
                <Select value={form.status || "Draft"} onValueChange={(v) => patch({ status: v })}>
                  <SelectTrigger className="w-40 h-8" data-testid="invoice-initial-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {INVOICE_STATUSES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        )}

        {/* Payment + status controls after save */}
        {saved && (
          <div className="mt-3 border border-dashed border-[#E5E5E5] rounded p-3 bg-[#F7F1E8]/40" data-testid="invoice-payment-panel">
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <span className="text-[10px] uppercase tracking-wider font-bold text-[#6C757D]">Current Status</span>
              <span
                className="px-2 py-1 rounded font-bold uppercase tracking-wider text-xs"
                style={{
                  backgroundColor: saved.status === "Paid" ? "#2A9D8F20" : saved.status === "Partially Paid" ? "#E85D0420" : saved.status === "Sent" ? "#1F4D3A20" : saved.status === "Cancelled" ? "#D6282820" : "#6C757D20",
                  color: saved.status === "Paid" ? "#2A9D8F" : saved.status === "Partially Paid" ? "#E85D04" : saved.status === "Sent" ? "#1F4D3A" : saved.status === "Cancelled" ? "#D62828" : "#6C757D",
                }}
                data-testid="invoice-current-status"
              >{saved.status}</span>
              <div className="ml-auto flex items-center gap-2">
                <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#6C757D]">Change to</Label>
                <Select value={saved.status} onValueChange={changeStatus}>
                  <SelectTrigger className="w-40 h-8" data-testid="invoice-change-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {INVOICE_STATUSES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <span className="text-[#6C757D]">Amount paid</span>
              <span className="font-bold text-[#1F4D3A]" data-testid="invoice-amount-paid">{formatEuro2(saved.amount_paid || 0)}</span>
              <span className="text-[#6C757D]">of</span>
              <span className="font-semibold">{formatEuro2(saved.total || 0)}</span>
              {saved.status !== "Paid" && (
                <>
                  <Input
                    type="number" min="0" step="0.01"
                    value={partialAmount}
                    onChange={(e) => setPartialAmount(e.target.value)}
                    className="w-32 h-8 ml-auto"
                    placeholder="Amount €"
                    data-testid="invoice-partial-amount"
                  />
                  <Button size="sm" variant="outline" onClick={registerPartial} data-testid="invoice-register-payment">
                    <Euro size={12} className="mr-1" /> Register
                  </Button>
                </>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="mt-4 flex flex-wrap gap-2">
          <Button variant="outline" onClick={onClose} data-testid="invoice-cancel">Close</Button>
          {saved ? (
            <>
              <Button variant="outline" onClick={download} data-testid="invoice-download-pdf">
                <Download size={14} className="mr-1" /> Download PDF
              </Button>
              <Button variant="outline" onClick={sendEmail} data-testid="invoice-send-email">
                <Mail size={14} className="mr-1" /> Send Invoice
              </Button>
              {saved.status !== "Paid" && (
                <Button onClick={markPaid} className="bg-[#2A9D8F] hover:bg-[#238a7e] text-white" data-testid="invoice-mark-paid">
                  <CheckCircle2 size={14} className="mr-1" /> Mark Paid
                </Button>
              )}
            </>
          ) : (
            <Button onClick={save} disabled={saving} className="bg-[#E85D04] hover:bg-[#c94b03] text-white" data-testid="invoice-save">
              {saving ? "Saving…" : existingInvoice ? "Update Invoice" : "Confirm & Create Invoice"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, children, className = "" }) => (
  <div className={className}>
    <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#6C757D]">{label}</Label>
    {children}
  </div>
);
